import { createPortal } from "react-dom";
import React, { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useLabText, GCC } from "../../lib/labText";
import { useLang } from "../../context/LanguageContext";

const field = "w-full bg-transparent border border-[#4A4A46] px-4 py-3 text-sm text-[#F2EBE1] outline-none focus:border-[#C49A6C] transition-colors";
const errMsg = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Error");

const Input = ({ k, c, setC, t, area }) => (area
  ? <textarea rows={2} value={c[k]} onChange={(e) => setC({ ...c, [k]: e.target.value })} placeholder={t(k)} data-testid={`lab-co-${k}`} className={field} />
  : <input value={c[k]} onChange={(e) => setC({ ...c, [k]: e.target.value })} placeholder={t(k)} data-testid={`lab-co-${k}`} className={field} />);

export const LabCheckout = ({ release, mode, participant, settings, user, onClose }) => {
  const { t, pick, aed } = useLabText();
  const { lang } = useLang();
  const [qty, setQty] = useState(1);
  const [picks, setPicks] = useState({});
  const [method, setMethod] = useState("land");
  const ships = settings.shipping || [];
  const startCountry = ships.find((x) => x.country === participant?.country)?.country || ships[0]?.country || "United Arab Emirates";
  const [c, setC] = useState({ full_name: participant?.full_name || user?.name || "", phone: participant?.phone || "", country: startCountry,
    city: participant?.city || "", zone: (ships.find((x) => x.country === startCountry)?.zones || []).some((z) => z.key === participant?.zone) ? participant.zone : "", address: "", notes_opt: "" });
  const ship = ships.find((x) => x.country === c.country) || {};
  const countryName = (en) => { const g = GCC.find((x) => x.en === en); return g ? (lang === "ar" ? g.ar : g.en) : en; };
  const [quote, setQuote] = useState(null);
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  const items = Object.entries(picks).filter(([, q]) => q > 0).map(([experiment_id, q]) => ({ experiment_id, qty: q }));
  const body = { release_id: release.id, mode, qty, items, shipping_method: method, customer: { ...c, notes: c.notes_opt } };
  const key = JSON.stringify([qty, items, method, c.country, c.zone]);

  useEffect(() => {
    if ((mode === "single" && !items.length) || (ship.zones && !c.zone)) { setQuote(null); return; }
    const h = setTimeout(() => api.post("/lab/quote", body).then((r) => setQuote(r.data)).catch((e) => { setQuote(null); toast.error(errMsg(e)); }), 250);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/lab/orders", { ...body, accept_terms: accept, lang, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const maxN = release.max_per_order || 1;
  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-end md:items-center justify-center md:p-6" onClick={onClose}>
      <div data-testid="lab-checkout" onClick={(e) => e.stopPropagation()} className="bg-[#0B0A08] border border-[#262421] w-full md:max-w-xl max-h-[94vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div><p className="text-[10px] uppercase tracking-[0.3em] text-[#C49A6C]" dir="ltr">{release.code}</p><h3 className="serif text-3xl mt-1">{t("checkout")}</h3></div>
          <button onClick={onClose} data-testid="lab-checkout-close" className="text-[#8A857D] hover:text-[#F2EBE1]"><X size={22} /></button>
        </div>
        {mode === "set" ? (
          <div className="mt-6 flex items-center justify-between border-b border-[#262421] pb-4">
            <span className="text-sm text-[#B8B0A4]">{t("qty_sets")}</span>
            <select value={qty} onChange={(e) => setQty(Number(e.target.value))} data-testid="lab-co-qty" className="bg-[#0B0A08] border border-[#4A4A46] px-3 py-2 text-sm">
              {Array.from({ length: Math.min(maxN, release.sets_available) }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        ) : (
          <div className="mt-6 space-y-2 border-b border-[#262421] pb-4">
            {release.experiments.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 text-sm">
                <span><b dir="ltr">{e.code}</b> · {pick(e, "name")} <span className="text-[#8A857D]" dir="ltr">· {aed(e.price)}</span></span>
                <select value={picks[e.id] || 0} onChange={(ev) => setPicks({ ...picks, [e.id]: Number(ev.target.value) })} data-testid={`lab-co-pick-${e.code}`} className="bg-[#0B0A08] border border-[#4A4A46] px-2 py-1.5">
                  {Array.from({ length: Math.min(maxN, e.available) + 1 }, (_, i) => i).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#8A857D] mt-6 mb-3">{t("delivery")}</p>
        <div className="grid gap-3">
          <Input k="full_name" c={c} setC={setC} t={t} />
          <Input k="phone" c={c} setC={setC} t={t} />
          <select value={c.country} onChange={(e) => setC({ ...c, country: e.target.value, zone: "" })} data-testid="lab-co-country" className={`${field} bg-[#0B0A08]`}>
            {ships.map((x) => <option key={x.country} value={x.country}>{countryName(x.country)}</option>)}
          </select>
          {ship.zones && (
            <select value={c.zone} onChange={(e) => setC({ ...c, zone: e.target.value })} data-testid="lab-co-zone" className={`${field} bg-[#0B0A08]`}>
              <option value="">{t("area")} · {t("choose")}</option>
              {ship.zones.map((z) => <option key={z.key} value={z.key}>{lang === "ar" ? z.ar : z.en}</option>)}
            </select>
          )}
          {c.country !== "United Arab Emirates" && ship.use_site && (
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="lab-co-method" className={`${field} bg-[#0B0A08]`}>
              <option value="land">{t("method")} · {t("land")}</option><option value="air">{t("method")} · {t("air")}</option>
            </select>
          )}
          <Input k="city" c={c} setC={setC} t={t} />
          <Input k="address" c={c} setC={setC} t={t} area />
          <Input k="notes_opt" c={c} setC={setC} t={t} area />
        </div>
        <div className="mt-6 border-t border-[#262421] pt-4 space-y-2 text-sm" data-testid="lab-co-summary">
          {quote ? (
            <>
              <div className="flex justify-between"><span className="text-[#B8B0A4]">{t("subtotal")}</span><span dir="ltr">{aed(quote.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#B8B0A4]">{t("shipping")}</span><span dir="ltr" data-testid="lab-co-shipping">{aed(quote.shipping)}</span></div>
              <div className="flex justify-between serif text-xl pt-2"><span>{t("total")}</span><span dir="ltr" data-testid="lab-co-total">{aed(quote.total)}</span></div>
            </>
          ) : mode === "single" && !items.length ? <p className="text-[#8A857D]" data-testid="lab-co-choose">{t("choose_one")}</p>
            : ship.zones && !c.zone ? <p className="text-[#8A857D]" data-testid="lab-co-choose-zone">{t("area")} · {t("choose")}</p>
            : <p className="text-[#8A857D] flex items-center gap-2"><Loader2 size={14} className="animate-spin" />{t("calculating")}</p>}
          <p className="text-[11px] text-[#6E685F]">{t("one_time")}</p>
        </div>
        <label className="flex items-start gap-3 mt-5 text-xs text-[#B8B0A4] leading-relaxed cursor-pointer">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="lab-co-accept" className="mt-0.5 accent-[#C49A6C]" />{t("accept")}
        </label>
        <button onClick={pay} disabled={!accept || !quote || busy} data-testid="lab-co-pay"
          className="w-full mt-6 bg-[#C49A6C] text-[#0B0A08] text-xs uppercase tracking-[0.25em] py-4 hover:bg-[#E7CBA9] transition-colors disabled:opacity-40">
          {busy ? <Loader2 size={16} className="animate-spin mx-auto" /> : t("pay")}
        </button>
      </div>
    </div>,
    document.body,
  );
};
